import { UnloadTilesPlugin } from '3d-tiles-renderer/three/plugins';
import {
  Box3,
  BufferAttribute,
  Camera,
  Intersection,
  Object3D,
  OrthographicCamera,
  Points,
  Ray,
  Raycaster,
  ShaderMaterial,
  Vector2,
  Vector3,
  WebGLRenderer,
} from 'three';

import { CloudFrontSigner } from '../../../../utils/cloud-front-signer/cloud-front-signer';
import { isDefined } from '../../../../utils/variables';
import { SkyviewCamera } from '../../../cameras/types';
import { CoordinateManager } from '../../../coordinate-manager';
import { closestToRay, toPointPositions } from '../../../utilities/pick-near-cursor';
import { PointCloud } from '../types';
import { CenteredLoadTilesRenderer } from './centered-load-tiles-renderer';
import { Cesium3dTilesPointsMaterial } from './cesium-3d-tiles-points-material';

export class Cesium3dTilesPointCloudRenderer extends PointCloud {
  /** The active renderer instance. Set on activate(), cleared on clearCustom(). */
  static current: Cesium3dTilesPointCloudRenderer | undefined;

  private tilesRenderer?: CenteredLoadTilesRenderer;
  private camera?: Camera;

  // Per-tile tracking.
  // tileScenes: tile object → its Three.js scene (used for dispose-model cleanup and debug).
  // tileMaterials: tile object → its ShaderMaterial (holds per-tile pointSize uniform).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private tileScenes = new Map<any, Object3D>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private tileMaterials = new Map<any, ShaderMaterial>();

  // Fallback point spacing (in the project's unit, like geometricError) used for leaf tiles
  // that have geometricError=0.
  // Tune via window.__pc.baseResolution.
  _baseResolution = 0.05;

  // World-space ray threshold (metres) used to gather candidate points for leaf tiles
  // (geometricError=0). Only needs to be generous — the real snap radius is enforced in
  // screen space (see pickNearCursor), so this just has to be wide enough to collect the
  // points the screen-space filter then narrows down.
  _navigationPickThreshold = 1.0;

  constructor(
    private signer: CloudFrontSigner,
    readonly origo: Vector3,
  ) {
    super();
  }

  async activate() {
    Cesium3dTilesPointCloudRenderer.current = this;
    const tilesetUrl = `${this.signer.getBaseUrl()}tileset.json`;

    this.tilesRenderer = new CenteredLoadTilesRenderer(tilesetUrl);

    // Tiles are in local project coordinates (easting/northing/height).
    // The scene origin represents the point cloud origo, so we offset the
    // group by -origo to bring tiles from absolute local coords to scene-relative coords.
    this.tilesRenderer.group.position.copy(this.origo.clone().negate());

    // LOD load/unload settings.
    // errorTarget: screen-space pixels of error tolerated before refining to a finer LOD.
    //   Library default is 16. Higher → must zoom closer before finer tiles load.
    // loadSiblings false → don't preemptively fetch tiles not yet in view.
    // optimizedLoadStrategy → prioritises tiles with the highest visual benefit per download.
    // UnloadTilesPlugin → actively disposes GPU resources for tiles that leave the viewport
    //   (old errorThreshold hysteresis was removed in the library; this replaces it).
    // lruCache sizes kept small so CPU-side eviction also kicks in promptly, set per camera in
    //   setCameraAndResolution.
    this.tilesRenderer.errorTarget = 46;
    this.tilesRenderer.loadSiblings = false;
    this.tilesRenderer.optimizedLoadStrategy = true;
    this.tilesRenderer.registerPlugin(new UnloadTilesPlugin({ delay: 250 }));

    this.tilesRenderer.addEventListener('load-model', (e: unknown) => {
      const { scene, tile } = e as { scene: Object3D; tile: unknown };
      this.applyMaterial(scene, tile);
    });

    this.tilesRenderer.addEventListener('dispose-model', (e: unknown) => {
      const { tile } = e as { tile: unknown };
      this.tileMaterials.get(tile)?.dispose();
      this.tileMaterials.delete(tile);
      this.tileScenes.delete(tile);
    });

    this.tilesRenderer.registerPlugin({
      preprocessURL: (uri: URL | string) => {
        const value = uri.toString();

        if (value.startsWith('blob:')) {
          return value;
        }

        const relativePath = value.replace(this.signer.getBaseUrl(), '');
        return this.signer.getSignedRequest(relativePath);
      },
    });

    this.add(this.tilesRenderer.group);

    return Promise.resolve();
  }

  /**
   * Applies a per-tile ShaderMaterial to all Points in a loaded tile scene.
   *
   * Each tile gets its own material instance so its `pointSize` uniform can be
   * updated independently. All other uniforms (displayClasses, lightDirection,
   * pointSizeScale, pointScale, …) are shared by reference with the singleton
   * and propagate automatically when changed globally.
   *
   * Reads NORMAL (Float32 VEC3) from the feature table and Classification
   * (UNSIGNED_BYTE SCALAR) from the batch table, attaching both as geometry
   * attributes for the shader.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private applyMaterial(scene: Object3D, tile: any) {
    this.tileScenes.set(tile, scene);

    const mat = Cesium3dTilesPointsMaterial.instance.createTileInstance(
      (tile.geometricError as number) ?? 1.0,
    );
    this.tileMaterials.set(tile, mat);

    const batchTable = (scene as any).batchTable;
    const featureTable = (scene as any).featureTable;

    // Classification is stored as UNSIGNED_BYTE SCALAR in the batch table.
    const rawClassification: ArrayLike<number> | null =
      (batchTable?.getPropertyArray('Classification') as ArrayLike<number> | null) ?? null;

    scene.traverse((obj) => {
      if (!(obj instanceof Points)) {
        return;
      }

      const geometry = obj.geometry;
      const count = geometry.attributes.position.count;

      // Normals — FLOAT VEC3 in the feature table.
      if (!geometry.attributes.normal && featureTable) {
        const rawNormals = featureTable.getData('NORMAL', count, 'FLOAT', 'VEC3') as
          | Float32Array
          | null
          | undefined;
        if (rawNormals) {
          // Copy into a fresh array so we don't hold a view into the tile buffer.
          geometry.setAttribute(
            'normal',
            new BufferAttribute(new Float32Array(rawNormals), 3, false),
          );
        }
      }

      if (!geometry.attributes.classification) {
        const classArray = new Float32Array(count);
        if (rawClassification) {
          for (let i = 0; i < count; i++) {
            classArray[i] = rawClassification[i];
          }
        }
        geometry.setAttribute('classification', new BufferAttribute(classArray, 1));
      }

      obj.material = mat;
    });
  }

  /**
   * Sets the `pointSize` uniform on every loaded tile's material.
   *
   * Each tile renders at its own geometricError (Cesium-aligned approach):
   *   coarse parent tiles → large splats, fine child tiles → small splats.
   * This fills spatial gaps in unloaded regions correctly. In fully-loaded areas
   * EDL provides the visual depth cues that make fine detail pop over the coarser
   * background, so no downward propagation is needed.
   *
   * Leaf tiles (geometricError=0) fall back to _baseResolution so they render
   * at a meaningful world-space size rather than a hard 1px.
   */
  private updateAdaptivePointSizes() {
    for (const [tile, mat] of this.tileMaterials) {
      const ownErr = (tile.geometricError as number) ?? 0;
      mat.uniforms.pointSize.value = ownErr > 0 ? ownErr : this._baseResolution;
    }
  }

  setCameraAndResolution(camera: Camera, renderer: WebGLRenderer) {
    if (!this.tilesRenderer) {
      return;
    }

    // setCamera only registers cameras, so a replaced camera would otherwise keep driving the LOD
    // selection, and load tiles for a view no longer shown
    if (this.camera && this.camera !== camera) {
      this.tilesRenderer.deleteCamera(this.camera);
    }
    this.camera = camera;
    this.tilesRenderer.setCamera(camera);

    // Looking straight down every tile in view needs the same LOD, so many more tiles are in use
    // than in perspective. Above maxSize, the cache unloads every tile that goes out of use, so
    // tiles would reload on each zoom out or pan back.
    const isOrthographic = (camera as OrthographicCamera).isOrthographicCamera === true;
    this.tilesRenderer.lruCache.maxSize = isOrthographic ? 400 : 100;
    this.tilesRenderer.lruCache.minSize = isOrthographic ? 200 : 50;

    // renderer.getSize() returns (0, 0) on the first few frames before the
    // canvas is fully sized.  Fall back to window dimensions to avoid the
    // "resolution not set" warning that suppresses LOD calculations.
    const size = new Vector2();
    renderer.getSize(size);
    if (size.x === 0 || size.y === 0) {
      size.set(window.innerWidth || 1280, window.innerHeight || 720);
    }

    size.multiplyScalar(renderer.getPixelRatio());
    this.tilesRenderer.setResolution(camera, size);

    // Keep screenHeight uniform in sync — required by the focal-length formula.
    Cesium3dTilesPointsMaterial.instance.shared.screenHeight.value = size.y;
  }

  update() {
    this.tilesRenderer?.update();
    this.updateAdaptivePointSizes();
  }

  async loadLodChunks(_camera: SkyviewCamera): Promise<void> {
    // Handled by TilesRenderer
  }

  cullLodChunks(_camera: SkyviewCamera): void {
    // Handled by TilesRenderer
  }

  async getMaxLodChunksFromRays(_rays: Ray[]): Promise<Points[]> {
    return Promise.resolve([]);
  }

  async getMaxLodChunksWithinBoundingBox(_bbox: Box3): Promise<Points[]> {
    return Promise.resolve([]);
  }

  /**
   * Picks the point closest to the ray, for rays that aren't from the camera. Rays from the camera
   * pick with pickNearCursor.
   */
  pick(ray: Raycaster): Intersection[] {
    const closest = closestToRay(this.intersect(ray));
    return closest ? [closest] : [];
  }

  intersect(ray: Raycaster): Intersection[] {
    if (!this.tilesRenderer) {
      return [];
    }

    // Ensure tile world matrices are current before raycasting. The tiles group is
    // re-positioned every frame (updatePointCloudPosition) and its child matrices are
    // managed by TilesRenderer, so between frames they can be stale relative to the ray.
    // three.js Points.raycast reads matrixWorld directly without refreshing it, so a
    // stale matrix silently misses. (CesiumPoints gets this refresh for free via
    // getWorldPosition() in its bounding-sphere pre-test.)
    this.tilesRenderer.group.updateWorldMatrix(true, true);

    const intersections: Intersection[] = [];
    const savedThreshold = ray.params.Points.threshold;

    for (const [tile, scene] of this.tileScenes) {
      if (!scene.visible) {
        continue;
      }

      // Gather generously in world space; the screen-space filter is the real gate.
      // Leaf tiles report geometricError=0, so fall back to _navigationPickThreshold.
      const tileErr = (tile.geometricError as number) ?? 0;
      ray.params.Points.threshold = tileErr > 0 ? tileErr : this._navigationPickThreshold;

      scene.traverse((obj) => {
        if (obj instanceof Points) {
          intersections.push(...ray.intersectObject(obj, false));
        }
      });
    }

    ray.params.Points.threshold = savedThreshold;
    return toPointPositions(ray, intersections);
  }

  available(): boolean {
    return true;
  }

  /** The bounds of the points in the scene. */
  getBoundingBox(): Box3 {
    if (!isDefined(this.tilesRenderer)) {
      return new Box3();
    }

    // In the tiles group's frame, the project's local coordinates, see activate
    const boundingBox = new Box3();
    const result = this.tilesRenderer.getBoundingBox(boundingBox);

    if (!result) {
      return new Box3();
    }

    const projectionOrigo = CoordinateManager.getInstance().getProjectionOrigo() ?? new Vector3();
    return boundingBox.translate(projectionOrigo.clone().negate());
  }

  getOrigo(): Vector3 {
    return this.origo;
  }

  clearCustom(): void {
    if (Cesium3dTilesPointCloudRenderer.current === this) {
      Cesium3dTilesPointCloudRenderer.current = undefined;
    }
    // Dispose per-tile materials. If tilesRenderer.dispose() fires dispose-model
    // events, those handlers will already have cleaned up most entries — the loop
    // catches any that were not signalled.
    for (const mat of this.tileMaterials.values()) {
      mat.dispose();
    }
    this.tileMaterials.clear();
    this.tileScenes.clear();

    this.tilesRenderer?.dispose();
    this.tilesRenderer = undefined;
    this.camera = undefined;
  }
}
